import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router';
import { Outlet } from 'react-router-dom';
import { Layout as DashboardLayout } from 'src/layouts/dashboard';
import { RoleGuard } from 'src/guards/auth-guard';
import { LockedOverlay } from 'src/components/locked-overlay';

const IndexPage = lazy(() => import('src/pages/dashboard/index'));

// Academy
const AcademyDashboardPage = lazy(() => import('src/pages/dashboard/academy/dashboard'));

// Customers
const CustomerListPage = lazy(() => import('src/pages/dashboard/customers/list'));
const CustomerPaidPage = lazy(() => import('src/pages/dashboard/customers/paid'));
const CustomerOldPage = lazy(() => import('src/pages/dashboard/customers/old'));
const Customer2026Page = lazy(() => import('src/pages/dashboard/customers/2026-leads'));
const CustomerImportPage = lazy(() => import('src/pages/dashboard/customers/import'));
const CustomerCreatePage = lazy(() => import('src/pages/dashboard/customers/create'));
const CustomerDetailPage = lazy(() => import('src/pages/dashboard/customers/detail'));
const CustomerEditPage = lazy(() => import('src/pages/dashboard/customers/edit'));

// Invoice
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoices/detail'));
const LeadInvoicesPage = lazy(() => import('src/pages/dashboard/lead-invoices'));
const LeadInvoiceDetailPage = lazy(() => import('src/pages/dashboard/lead-invoice-detail'));


// Products
const ProductListPage = lazy(() => import('src/pages/dashboard/products/list'));
const ProductCreatePage = lazy(() => import('src/pages/dashboard/products/create'));

// Create Hub
const CreatePage = lazy(() => import('src/pages/dashboard/create'));

// Documents Hub
const DocumentsPage = lazy(() => import('src/pages/dashboard/documents'));

// Create Email Quote
const QuoteEmailTemplatePage = lazy(() => import('src/pages/dashboard/crm'));
const UpdateEmailTemplatePage = lazy(() => import('src/pages/dashboard/crm/update'));
const WebInvoicePage = lazy(() => import('src/pages/dashboard/web-invoice'));
const WebQuotePage = lazy(() => import('src/pages/dashboard/web-quote'));
const SendEmailPage = lazy(() => import('src/pages/dashboard/send-email'));
const InboxPage = lazy(() => import('src/pages/dashboard/inbox'));
const SupportHubPage = lazy(() => import('src/pages/dashboard/support-hub'));
const EnrichmentPage = lazy(() => import('src/pages/dashboard/enrichment'));
const ExhaustLeadPage = lazy(() => import('src/pages/dashboard/exhaust-lead'));
const ExhaustOldLeadPage = lazy(() => import('src/pages/dashboard/exhaust-old-lead'));
const SalesTrainingPage = lazy(() => import('src/pages/dashboard/sales-training'));
const SalesPresentationPage = lazy(() => import('src/pages/dashboard/sales-presentation'));
const SalesScriptsPage = lazy(() => import('src/pages/dashboard/sales-scripts'));
const SendTextsPage = lazy(() => import('src/pages/dashboard/send-texts'));
const WorkflowsPage = lazy(() => import('src/pages/dashboard/workflows'));
const TasksPage = lazy(() => import('src/pages/dashboard/tasks'));

// Schedule Calendar
const ScheduleCalendarPage = lazy(() => import('src/pages/dashboard/schedule-calendar'));

// Image URL Tool
const ImageUrlToolPage = lazy(() => import('src/pages/dashboard/image-url-tool'));

// Downloads
const DownloadsPage = lazy(() => import('src/pages/dashboard/downloads'));

// Cloud
const CloudPage = lazy(() => import('src/pages/dashboard/cloud'));

// Inventory
const InventoryPage = lazy(() => import('src/pages/dashboard/inventory'));

// Contractors
const ContractorsPage = lazy(() => import('src/pages/dashboard/employee-hub'));

// Company Contracts
const CompanyContractsPage = lazy(() => import('src/pages/dashboard/contracts'));

// Company Hub
const CompanyHubPage = lazy(() => import('src/pages/dashboard/company'));
const CompanyTeamPage = lazy(() => import('src/pages/dashboard/company/team'));
const CompanyFilesPage = lazy(() => import('src/pages/dashboard/company/files'));

// Sheet
const SheetPage = lazy(() => import('src/pages/dashboard/sheet'));

// Reports
const ReportsPage = lazy(() => import('src/pages/dashboard/reports'));
const LogsPage = lazy(() => import('src/pages/dashboard/logs'));

// Recording Tool
const RecordingToolPage = lazy(() => import('src/pages/dashboard/recording-tool'));

// Sales Recordings
const SalesRecordingsPage = lazy(() => import('src/pages/dashboard/sales-recordings'));

// Custom Quote
const CustomQuotePage = lazy(() => import('src/pages/dashboard/custom-quote'));
const QuotesListPage = lazy(() => import('src/pages/dashboard/quotes'));

// Gift Card
const GiftCardPage = lazy(() => import('src/pages/dashboard/giftcard'));

// Tools
const ToolsPage = lazy(() => import('src/pages/dashboard/tools'));

// QuickBooks
const QuickBooksPage = lazy(() => import('src/pages/dashboard/quickbooks'));

// Marketing
const MarketingCampaignsPage = lazy(() => import('src/pages/dashboard/marketing/campaigns'));
const MarketingAnalyticsPage = lazy(() => import('src/pages/dashboard/marketing/analytics'));
const MarketingSocialMediaPage = lazy(() => import('src/pages/dashboard/marketing/social-media'));
const SocialAnalyticsPage = lazy(() => import('src/pages/dashboard/marketing/social-analytics'));
const FacebookAnalyticsPage = lazy(() => import('src/pages/dashboard/marketing/facebook-analytics'));
const InstagramAnalyticsPage = lazy(() => import('src/pages/dashboard/marketing/instagram-analytics'));

// Sales Dashboard
const SalesDashboardPage = lazy(() => import('src/pages/dashboard/sales-dashboard'));
const SalesPipelinePage = lazy(() => import('src/pages/dashboard/sales-pipeline'));

// Service Agreement
const SignedAgreementsPage = lazy(() => import('src/pages/dashboard/signed-agreements'));

// Testimonials
const TestimonialsPage = lazy(() => import('src/pages/dashboard/testimonials'));

// Branding
const BrandingPage = lazy(() => import('src/pages/dashboard/branding'));

// Make.com Integrations
const MakePage = lazy(() => import('src/pages/dashboard/make'));
const MakeScenarioPage = lazy(() => import('src/pages/dashboard/make/scenario'));

// Zapier
const ZapierPage = lazy(() => import('src/pages/dashboard/zapier'));

// n8n
const N8nPage = lazy(() => import('src/pages/dashboard/n8n'));

// Accounting
const AccountingIndexPage = lazy(() => import('src/pages/dashboard/accounting/index'));
const AccountingRevenuePage = lazy(() => import('src/pages/dashboard/accounting/revenue'));
const AccountingExpensesPage = lazy(() => import('src/pages/dashboard/accounting/expenses'));
const AccountingPayrollPage = lazy(() => import('src/pages/dashboard/accounting/payroll'));
const AccountingReportsPage = lazy(() => import('src/pages/dashboard/accounting/reports'));

// Atlas AI
const AtlasPage = lazy(() => import('src/pages/dashboard/atlas'));

// Lead Generation
const LeadGenerationPage = lazy(() => import('src/pages/dashboard/lead-generation'));

// Database Moat
const DatabaseMoatPage = lazy(() => import('src/pages/dashboard/database-moat'));

// Knowledge Base
const KnowledgeBasePage = lazy(() => import('src/pages/dashboard/knowledge-base'));

// Settings
const SettingsPage = lazy(() => import('src/pages/dashboard/settings'));

// Cold Prospecting
const ColdProspectingPage = lazy(() => import('src/pages/dashboard/cold-prospecting'));

// ElevenLabs
const ElevenLabsPage = lazy(() => import('src/pages/dashboard/elevenlabs'));

// Andrea (AI Receptionist)
const AndreaChatPage = lazy(() => import('src/pages/dashboard/andrea/chat'));
const AndreaCallLogPage = lazy(() => import('src/pages/dashboard/andrea/call-log'));
const AndreaMessageLogPage = lazy(() => import('src/pages/dashboard/andrea/message-log'));
const AndreaSettingsPage = lazy(() => import('src/pages/dashboard/andrea/config'));

// Control
const ControlOverviewPage = lazy(() => import('src/pages/dashboard/control/overview'));
const ControlChannelsPage = lazy(() => import('src/pages/dashboard/control/channels'));
const ControlInstancesPage = lazy(() => import('src/pages/dashboard/control/instances'));
const ControlSessionsPage = lazy(() => import('src/pages/dashboard/control/sessions'));
const ControlCronJobsPage = lazy(() => import('src/pages/dashboard/control/cron-jobs'));
const ControlMemoryPage = lazy(() => import('src/pages/dashboard/control/memory'));

// Agent Settings
const AgentSettingsConfigPage = lazy(() => import('src/pages/dashboard/agent-settings/config'));
const AgentSettingsDebugPage = lazy(() => import('src/pages/dashboard/agent-settings/debug'));
const AgentSettingsLogsPage = lazy(() => import('src/pages/dashboard/agent-settings/logs'));

// Other
const AccountPage = lazy(() => import('src/pages/dashboard/account'));
const AnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));
const InsightsPage = lazy(() => import('src/pages/dashboard/insights'));

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: (
      <DashboardLayout>
        <Suspense>
          <RoleGuard>
            <Outlet />
          </RoleGuard>
        </Suspense>
      </DashboardLayout>
    ),
    children: [
      {
        index: true,
        element: <IndexPage />
      },
      {
        path: 'insights',
        element: <InsightsPage />
      },
      {
        path: 'sales-dashboard',
        element: <LockedOverlay><SalesDashboardPage /></LockedOverlay>
      },
      {
        path: 'sales-pipeline',
        element: <SalesPipelinePage />
      },
      {
        path: 'training',
        element: <AcademyDashboardPage />
      },
      {
        path: 'customers',
        children: [
          {
            index: true,
            element: <CustomerListPage />
          },
          {
            path: 'paid',
            element: <CustomerPaidPage />
          },
          {
            path: '2025-leads',
            element: <CustomerOldPage />
          },
          {
            path: '2026-leads',
            element: <Customer2026Page />
          },
          {
            path: 'import',
            element: <CustomerImportPage />
          },
          {
            path: 'create',
            element: <CustomerCreatePage />
          },
          {
            path: ':customerId',
            element: <CustomerDetailPage />
          },
          {
            path: ':customerId/edit',
            element: <CustomerEditPage />
          }
        ]
      },
      {
        path: 'invoices',
        element: <InvoiceCreatePage />
      },
      {
        path: 'lead-invoices',
        children: [
          {
            index: true,
            element: <LeadInvoicesPage />
          },
          {
            path: ':invoiceId',
            element: <LeadInvoiceDetailPage />
          }
        ]
      },
      {
        path: 'products',
        children: [
          {
            index: true,
            element: <ProductListPage />
          },
          {
            path: 'create',
            element: <ProductCreatePage />
          }
        ]
      },
      {
        path: 'account',
        element: <AccountPage />
      },
      {
        path: 'analytics',
        element: <LockedOverlay><AnalyticsPage /></LockedOverlay>
      },
      {
        path: 'create',
        element: <CreatePage />
      },
      {
        path: 'documents',
        element: <DocumentsPage />
      },
      {
        path: 'create-email',
        children: [
          {
            path: 'quote',
            element: <QuoteEmailTemplatePage />
          },
          {
            path: 'update',
            element: <UpdateEmailTemplatePage />
          }
        ]
      },
      {
        path: 'invoice',
        element: <WebInvoicePage />
      },
      {
        path: 'quote',
        element: <WebQuotePage />
      },
      {
        path: 'send-email',
        element: <SendEmailPage />
      },
      {
        path: 'inbox',
        element: <InboxPage />
      },
      {
        path: 'support-hub',
        element: <SupportHubPage />
      },
      {
        path: 'enrichment',
        element: <LockedOverlay><EnrichmentPage /></LockedOverlay>
      },
      {
        path: 'exhaust-lead',
        element: <ExhaustLeadPage />
      },
      {
        path: 'exhaust-old-lead',
        element: <ExhaustOldLeadPage />
      },
      {
        path: 'sales-training',
        element: <SalesTrainingPage />
      },
      {
        path: 'sales-presentation',
        element: <SalesPresentationPage />
      },
      {
        path: 'sales-scripts',
        element: <LockedOverlay><SalesScriptsPage /></LockedOverlay>
      },
      {
        path: 'send-texts',
        element: <LockedOverlay><SendTextsPage /></LockedOverlay>
      },
      {
        path: 'workflows',
        element: <WorkflowsPage />
      },
      {
        path: 'tasks',
        element: <TasksPage />
      },
      {
        path: 'calendar',
        element: <ScheduleCalendarPage />
      },
      {
        path: 'image-url-tool',
        element: <LockedOverlay><ImageUrlToolPage /></LockedOverlay>
      },
      {
        path: 'downloads',
        element: <DownloadsPage />
      },
      {
        path: 'cloud',
        element: <CloudPage />
      },
      {
        path: 'inventory',
        element: <InventoryPage />
      },
      {
        path: 'contractors',
        element: <ContractorsPage />
      },
      {
        path: 'contracts',
        element: <CompanyContractsPage />
      },
      {
        path: 'company',
        children: [
          {
            index: true,
            element: <CompanyHubPage />
          },
          {
            path: 'team',
            element: <CompanyTeamPage />
          },
          {
            path: 'files',
            element: <CompanyFilesPage />
          }
        ]
      },
      {
        path: 'sheet',
        element: <SheetPage />
      },
      {
        path: 'reports',
        element: <ReportsPage />
      },
      {
        path: 'logs',
        element: <LogsPage />
      },
      {
        path: 'recording-tool',
        element: <RecordingToolPage />
      },
      {
        path: 'sales-recordings',
        element: <SalesRecordingsPage />
      },
      {
        path: 'custom-quote',
        element: <CustomQuotePage />
      },
      {
        path: 'quotes',
        element: <QuotesListPage />
      },
      {
        path: 'giftcard',
        element: <GiftCardPage />
      },
      {
        path: 'tools',
        element: <ToolsPage />
      },
      {
        path: 'quickbooks',
        children: [
          {
            index: true,
            element: <QuickBooksPage />
          },
          {
            path: 'callback',
            element: <QuickBooksPage />
          }
        ]
      },
      {
        path: 'marketing',
        children: [
          {
            index: true,
            element: <LockedOverlay><MarketingCampaignsPage /></LockedOverlay>
          },
          {
            path: 'analytics',
            element: <MarketingAnalyticsPage />
          },
          {
            path: 'social-media',
            element: <LockedOverlay><MarketingSocialMediaPage /></LockedOverlay>
          },
          {
            path: 'social-analytics',
            element: <SocialAnalyticsPage />
          },
          {
            path: 'facebook-analytics',
            element: <FacebookAnalyticsPage />
          },
          {
            path: 'instagram-analytics',
            element: <InstagramAnalyticsPage />
          }
        ]
      },
      {
        path: 'signed-agreements',
        element: <SignedAgreementsPage />
      },
      {
        path: 'testimonials',
        element: <TestimonialsPage />
      },
      {
        path: 'branding',
        element: <BrandingPage />
      },
      {
        path: 'make',
        children: [
          {
            index: true,
            element: <MakePage />
          },
          {
            path: ':scenarioId',
            element: <MakeScenarioPage />
          }
        ]
      },
      {
        path: 'zapier',
        element: <LockedOverlay><ZapierPage /></LockedOverlay>
      },
      {
        path: 'n8n',
        element: <LockedOverlay><N8nPage /></LockedOverlay>
      },
      {
        path: 'accounting',
        children: [
          {
            index: true,
            element: <AccountingIndexPage />
          },
          {
            path: 'revenue',
            element: <AccountingRevenuePage />
          },
          {
            path: 'expenses',
            element: <AccountingExpensesPage />
          },
          {
            path: 'payroll',
            element: <AccountingPayrollPage />
          },
          {
            path: 'reports',
            element: <AccountingReportsPage />
          }
        ]
      },
      {
        path: 'atlas',
        element: <LockedOverlay><AtlasPage /></LockedOverlay>
      },
      {
        path: 'lead-generation',
        element: <LockedOverlay><LeadGenerationPage /></LockedOverlay>
      },
      {
        path: 'database-moat',
        element: <DatabaseMoatPage />
      },
      {
        path: 'knowledge-base',
        element: <KnowledgeBasePage />
      },
      {
        path: 'settings',
        element: <SettingsPage />
      },
      {
        path: 'cold-prospecting',
        element: <ColdProspectingPage />
      },
      {
        path: 'elevenlabs',
        element: <ElevenLabsPage />
      },
      {
        path: 'andrea',
        children: [
          {
            index: true,
            element: <AndreaChatPage />
          },
          {
            path: 'chat',
            element: <AndreaChatPage />
          },
          {
            path: 'call-log',
            element: <AndreaCallLogPage />
          },
          {
            path: 'message-log',
            element: <AndreaMessageLogPage />
          },
          {
            path: 'config',
            element: <AndreaSettingsPage />
          }
        ]
      },
      {
        path: 'control',
        children: [
          {
            path: 'overview',
            element: <ControlOverviewPage />
          },
          {
            path: 'channels',
            element: <ControlChannelsPage />
          },
          {
            path: 'instances',
            element: <ControlInstancesPage />
          },
          {
            path: 'sessions',
            element: <ControlSessionsPage />
          },
          {
            path: 'cron-jobs',
            element: <ControlCronJobsPage />
          },
          {
            path: 'memory',
            element: <ControlMemoryPage />
          }
        ]
      },
      {
        path: 'agent-settings',
        children: [
          {
            path: 'config',
            element: <AgentSettingsConfigPage />
          },
          {
            path: 'debug',
            element: <AgentSettingsDebugPage />
          },
          {
            path: 'logs',
            element: <AgentSettingsLogsPage />
          }
        ]
      },
    ]
  }
];
